import React from 'react';

import { cssMap } from '@compiled/react';
import noop from 'lodash/noop';
import { token } from '@atlaskit/tokens';

import ButtonGroup from '@atlaskit/button/button-group';
import Button, { LinkButton } from '@atlaskit/button/new';
import Form, { FormFooter, FormHeader } from '@atlaskit/form';

import { Text, Stack } from '@atlaskit/primitives/compiled';
import { useIntl } from '@atlassian/jira-intl';

import { lookupTypeByTypeId } from '../../../../common/application-types.tsx';
import ProductCard from '../../../common/product-card/index.tsx';
import {
	getDefaultCreationState,
	generateApplinksCreationRedirectionUrl,
} from '../../../../common/interchange.tsx';
import type { StageReciprocalPromptProps } from '../../../../common/types.tsx';

const styles = cssMap({
	linkDisplay: {
		marginTop: token('space.200'),
		marginBottom: token('space.200'),
	},
});

type ReciprocalPromptSubpageProps = {
	state: StageReciprocalPromptProps;
};

const ReciprocalPromptSubpage = (props: ReciprocalPromptSubpageProps) => {
	useIntl();

	const thisApplicationManifest = props.state.localManifest;
	const targetApplicationManifest = props.state.remoteManifest;

	const targetCloudId = props.state.remoteManifest.id;
	const thisBaseUrl = thisApplicationManifest.url;
	const thisBaseTypeId = thisApplicationManifest.typeId;

	const creationState = getDefaultCreationState(thisBaseUrl, thisBaseTypeId, targetCloudId);

	const reciprocalLinkUrl = generateApplinksCreationRedirectionUrl(
		targetApplicationManifest.url,
		targetApplicationManifest.typeId,
		creationState,
	);

	return (
		<Form<{ username: string; password: string; remember: boolean }> onSubmit={noop}>
			{({ formProps }) => (
				<form {...formProps}>
					<FormHeader title="Create reciprocal link">
						<Text>
							A link from Jira to {targetApplicationManifest.name} has been established. You will
							need to navigate to {targetApplicationManifest.name} to complete the link.
						</Text>
					</FormHeader>

					<Stack xcss={styles.linkDisplay} space="space.200">
						<ProductCard
							// TODO: Determine if the type is DC or Cloud
							applicationType={lookupTypeByTypeId(targetApplicationManifest.typeId, false)}
							displayUrl={targetApplicationManifest.url}
							heading="Application Link"
							connectedStatus="partially-connected"
						/>
					</Stack>

					<FormFooter>
						<ButtonGroup label="Form submit options">
							<Button appearance="subtle">Cancel</Button>
							<LinkButton href={reciprocalLinkUrl} appearance="primary">
								{`Continue on ${targetApplicationManifest.name}`}
							</LinkButton>
						</ButtonGroup>
					</FormFooter>
				</form>
			)}
		</Form>
	);
};

export default ReciprocalPromptSubpage;
